import type {
    AiAnalysisProgressModel,
    AiAnalysisProgressResponse,
    AuditDataModel,
    AuditDataResponse,
    CancelDevelopmentParams,
    CreateDevelopmentParams,
    CreateEstimateParams,
    CreateSolicitationParams,
    DevelopmentDetailDataModel,
    DevelopmentDetailDocumentResponse,
    DevelopmentDocumentModel,
    DevelopmentDocumentResponse,
    DevelopmentSummaryModel,
    EfVersionModel,
    EfVersionResponse,
    ImpedeDevelopmentParams,
    JiraEpicLink,
    JiraEpicSuggestion,
    JiraNoPairResult,
    PbiDecompositionModel,
    PbiDecompositionResponse,
    StartDevelopmentParams,
    UpdateDevelopmentParams,
    ValidationEvidenceModel,
    ValidationEvidenceResponse
} from '@/domain/models/development.js';
import {
    AiAnalysisProgressModel as AiAnalysisProgressModelClass,
    AuditDataModel as AuditDataModelClass,
    DevelopmentDetailDataModel as DevelopmentDetailDataModelClass,
    DevelopmentDocumentModel as DevelopmentDocumentModelClass,
    DevelopmentSummaryModel as DevelopmentSummaryModelClass,
    EfVersionModel as EfVersionModelClass,
    PbiDecompositionModel as PbiDecompositionModelClass,
    ValidationEvidenceModel as ValidationEvidenceModelClass
} from '@/domain/models/development.js';
import type { SolicitationEvidenceModel, SolicitationEvidenceResponse } from '@/domain/models/solicitation-evidence.js';
import { SolicitationEvidenceModel as SolicitationEvidenceModelClass } from '@/domain/models/solicitation-evidence.js';
import type { HttpClient } from '@/domain/protocols/http-client.js';
import type { DevelopmentRepository } from '@/domain/repositories/development-repository.js';
import type { ImportDevelopmentsResult } from '@/domain/use-cases/developments.js';
import { parseDevelopmentDetail, parseDevelopmentSummaryList } from '@/infra/contracts/development-contract.js';

// O backend devolve { code, details: [...] }: o primeiro detalhe e o resumo e os
// seguintes sao os erros por linha. Sem juntar isso o usuario so veria "Import failed".
const buildImportErrorMessage = (data: { message?: string; details?: { message?: string }[] } | null): string => {
    const messages = (data?.details ?? []).map((d) => d.message).filter((m): m is string => typeof m === 'string' && m.trim() !== '');
    if (messages.length > 0) {
        return messages.join('\n');
    }
    return data?.message ?? 'Falha ao importar a planilha';
};

const parseDispositionFileName = (disposition: string | null, fallback: string): string => {
    const encoded = disposition?.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
    if (encoded) {
        try {
            return decodeURIComponent(encoded.trim());
        } catch {
            return fallback;
        }
    }
    return disposition?.match(/filename="([^"]+)"/)?.[1] ?? fallback;
};

export class DevelopmentRepositoryImpl implements DevelopmentRepository {
    constructor(private readonly httpClient: HttpClient) {}

    public async findAll(params?: {
        search?: string;
        statuses?: string[];
        projectId?: string;
        page?: number;
        limit?: number;
        includeCanceled?: boolean;
    }): Promise<{ data: DevelopmentSummaryModel[]; total: number; page: number; totalPages: number }> {
        const { statuses, ...rest } = params ?? {};
        const qs = this.buildQs({ ...rest, status: statuses });
        const response = await this.httpClient.request<unknown>({ url: `/api/developments?${qs}`, method: 'GET' });
        const parsed = parseDevelopmentSummaryList(response.body);
        return {
            data: DevelopmentSummaryModelClass.withFromResponseList(parsed.data),
            total: parsed.total,
            page: parsed.page,
            totalPages: parsed.totalPages
        };
    }

    public async exportExcel(params?: { search?: string; statuses?: string[]; projectId?: string; includeCanceled?: boolean }): Promise<{ blob: Blob; filename: string }> {
        const { statuses, ...rest } = params ?? {};
        const qs = this.buildQs({ ...rest, status: statuses });
        const res = await fetch(`/api/developments/export?${qs}`, {
            method: 'GET',
            headers: this.authHeaders()
        });
        if (res.status === 401) {
            throw new Error('Unauthorized');
        }
        if (!res.ok) {
            const data = (await res.json().catch(() => null)) as { message?: string } | null;
            throw new Error(data?.message ?? 'Export failed');
        }
        const blob = await res.blob();
        const disposition = res.headers.get('Content-Disposition');
        const match = disposition?.match(/filename="?([^"]+)"?/);
        return { blob, filename: match?.[1] ?? 'desenvolvimentos.xls' };
    }

    public async importExcel(file: File, projectId: string): Promise<ImportDevelopmentsResult> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('projectId', projectId);
        const res = await fetch('/api/developments/import', {
            method: 'POST',
            body: formData,
            headers: this.authHeaders()
        });
        if (res.status === 401) {
            throw new Error('Unauthorized');
        }
        const data = (await res.json().catch(() => null)) as (ImportDevelopmentsResult & { message?: string; details?: { message?: string }[] }) | null;
        if (!res.ok || !data) {
            throw new Error(buildImportErrorMessage(data));
        }
        return {
            created: data.created ?? 0,
            displayIds: data.displayIds ?? [],
            skipped: data.skipped ?? 0,
            warnings: data.warnings ?? []
        };
    }

    public async findById(id: string): Promise<DevelopmentDetailDataModel> {
        const response = await this.httpClient.request<unknown>({ url: `/api/developments/${encodeURIComponent(id)}`, method: 'GET' });
        const parsed = parseDevelopmentDetail(response.body);
        return DevelopmentDetailDataModelClass.withFromResponse(parsed);
    }

    public async create(params: CreateDevelopmentParams): Promise<{ id: string; jiraWarning: string | null }> {
        const response = await this.httpClient.request<{ id: string; jiraWarning?: string | null }>({ url: '/api/developments', method: 'POST', body: params });
        return { id: response.body.id, jiraWarning: response.body.jiraWarning ?? null };
    }

    public async update(id: string, params: UpdateDevelopmentParams): Promise<void> {
        await this.httpClient.request({ url: `/api/developments/${encodeURIComponent(id)}`, method: 'PUT', body: params });
    }

    public async cancel(id: string, params: CancelDevelopmentParams): Promise<void> {
        await this.httpClient.request({
            url: `/api/developments/${encodeURIComponent(id)}/cancel`,
            method: 'PATCH',
            body: { cancelReasonId: params.reasonId, cancelReason: params.justification }
        });
    }

    public async reactivate(id: string): Promise<void> {
        await this.httpClient.request({ url: `/api/developments/${encodeURIComponent(id)}/reactivate`, method: 'PATCH' });
    }

    public async impede(id: string, params: ImpedeDevelopmentParams): Promise<void> {
        await this.httpClient.request({
            url: `/api/developments/${encodeURIComponent(id)}/impede`,
            method: 'PATCH',
            body: { impedimentReasonId: params.reasonId, impedimentDescription: params.description }
        });
    }

    public async resume(id: string): Promise<void> {
        await this.httpClient.request({ url: `/api/developments/${encodeURIComponent(id)}/resume`, method: 'PATCH' });
    }

    public async delete(id: string): Promise<void> {
        await this.httpClient.request({ url: `/api/developments/${encodeURIComponent(id)}`, method: 'DELETE' });
    }

    public async start(id: string, params: StartDevelopmentParams): Promise<void> {
        await this.httpClient.request({ url: `/api/developments/${encodeURIComponent(id)}/start`, method: 'POST', body: params });
    }

    public async uploadEf(developmentId: string, file: File): Promise<EfVersionModel> {
        const formData = new FormData();
        formData.append('file', file);
        const res = await fetch(`/api/developments/${encodeURIComponent(developmentId)}/ef`, {
            method: 'POST',
            body: formData,
            headers: this.authHeaders()
        });
        const data = await res.json().catch(() => null);
        if (!res.ok || !data) {
            throw new Error(data?.message ?? 'Upload failed');
        }
        return EfVersionModelClass.withFromResponse(data as EfVersionResponse);
    }

    public async deleteEf(developmentId: string): Promise<void> {
        await this.httpClient.request<{ message: string }>({
            url: `/api/developments/${encodeURIComponent(developmentId)}/ef`,
            method: 'DELETE'
        });
    }

    public async downloadEf(developmentId: string, version?: number): Promise<{ blob: Blob; filename: string }> {
        const response = await this.httpClient.request<Blob>({
            url: `/api/developments/${encodeURIComponent(developmentId)}/ef/download${version ? `?version=${version}` : ''}`,
            method: 'GET',
            responseType: 'blob'
        });
        return { blob: response.body, filename: parseDispositionFileName(response.headers?.['content-disposition'] ?? null, 'especificacao-funcional.docx') };
    }

    public async requestAiAnalysis(developmentId: string): Promise<{ attemptNumber: number }> {
        const response = await this.httpClient.request<{ message: string; attemptNumber: number }>({
            url: `/api/developments/${encodeURIComponent(developmentId)}/ai-analysis`,
            method: 'POST'
        });
        return { attemptNumber: response.body.attemptNumber };
    }

    public async sendToDevelopment(developmentId: string): Promise<void> {
        await this.httpClient.request({ url: `/api/developments/${encodeURIComponent(developmentId)}/send-to-development`, method: 'POST' });
    }

    public async createEstimate(developmentId: string, params: CreateEstimateParams): Promise<void> {
        await this.httpClient.request({
            url: `/api/developments/${encodeURIComponent(developmentId)}/estimates`,
            method: 'POST',
            body: params
        });
    }

    public async approveEstimate(developmentId: string): Promise<void> {
        await this.httpClient.request({ url: `/api/developments/${encodeURIComponent(developmentId)}/estimates/approve`, method: 'POST' });
    }

    public async rejectEstimate(developmentId: string, reasonId: string, notes?: string): Promise<void> {
        await this.httpClient.request({ url: `/api/developments/${encodeURIComponent(developmentId)}/estimates/reject`, method: 'POST', body: { reasonId, notes } });
    }

    public async submitEstimate(_developmentId: string): Promise<void> {
        throw new Error('submitEstimate has no backend route; use createEstimate instead (see T17 gap list)');
    }

    public async returnEstimateToAnalysis(developmentId: string, reasonId: string, notes?: string): Promise<void> {
        await this.httpClient.request({ url: `/api/developments/${encodeURIComponent(developmentId)}/return-ef`, method: 'POST', body: { reasonId, notes } });
    }

    public async assignTechLead(developmentId: string, techLeadId: string): Promise<void> {
        await this.httpClient.request({ url: `/api/developments/${encodeURIComponent(developmentId)}/assign-tech-lead`, method: 'POST', body: { techLeadId } });
    }

    public async setDevelopers(developmentId: string, developers: Array<{ userId: string; isLead: boolean }>): Promise<void> {
        await this.httpClient.request({ url: `/api/developments/${encodeURIComponent(developmentId)}/developers`, method: 'POST', body: { developers } });
    }

    public async removeDeveloper(_developmentId: string, _developerId: string): Promise<void> {
        throw new Error('removeDeveloper has no backend route; use setDevelopers instead (see T17 gap list)');
    }

    public async makeDeveloperLead(_developmentId: string, _developerId: string): Promise<void> {
        throw new Error('makeDeveloperLead has no backend route; use setDevelopers instead (see T17 gap list)');
    }

    public async uploadDocument(developmentId: string, type: string, file: File): Promise<DevelopmentDocumentModel> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', type);
        const res = await fetch(`/api/developments/${encodeURIComponent(developmentId)}/documents`, {
            method: 'POST',
            body: formData,
            headers: this.authHeaders()
        });
        const data = await res.json().catch(() => null);
        if (!res.ok || !data) {
            throw new Error(data?.message ?? 'Upload failed');
        }
        return DevelopmentDocumentModelClass.withFromResponse(data as DevelopmentDocumentResponse);
    }

    public async downloadDocument(developmentId: string, documentId: string): Promise<{ url: string }> {
        const response = await this.httpClient.request<{ url: string }>({
            url: `/api/developments/${encodeURIComponent(developmentId)}/documents/${encodeURIComponent(documentId)}/download-url`,
            method: 'GET'
        });
        return response.body;
    }

    public async finishDev(developmentId: string, endDate: string): Promise<void> {
        await this.httpClient.request({ url: `/api/developments/${encodeURIComponent(developmentId)}/finish-dev`, method: 'POST', body: { endDate } });
    }

    public async toggleCutoverNotRequired(developmentId: string, cutoverNotRequired: boolean): Promise<void> {
        await this.httpClient.request({ url: `/api/developments/${encodeURIComponent(developmentId)}/cutover-toggle`, method: 'PATCH', body: { cutoverNotRequired } });
    }

    public async approveDocumentation(developmentId: string): Promise<void> {
        await this.httpClient.request({ url: `/api/developments/${encodeURIComponent(developmentId)}/documents/approve`, method: 'POST' });
    }

    public async rejectDocumentation(developmentId: string, reasonId: string, notes?: string): Promise<void> {
        await this.httpClient.request({
            url: `/api/developments/${encodeURIComponent(developmentId)}/documents/reject`,
            method: 'POST',
            body: { reasonId, notes }
        });
    }

    public async uploadValidationEvidence(developmentId: string, file: File, type: 'cf' | 'uc'): Promise<ValidationEvidenceModel> {
        const formData = new FormData();
        formData.append('file', file);
        const res = await fetch(`/api/developments/${encodeURIComponent(developmentId)}/validation/evidences?type=${type}`, {
            method: 'POST',
            body: formData,
            headers: this.authHeaders()
        });
        const data = await res.json().catch(() => null);
        if (!res.ok || !data) {
            throw new Error(data?.message ?? 'Upload failed');
        }
        return ValidationEvidenceModelClass.withFromResponse(data as ValidationEvidenceResponse);
    }

    public async removeValidationEvidence(developmentId: string, type: 'cf' | 'uc', evidenceId: string): Promise<void> {
        await this.httpClient.request({
            url: `/api/developments/${encodeURIComponent(developmentId)}/validation/${type}-evidence/${encodeURIComponent(evidenceId)}`,
            method: 'DELETE'
        });
    }

    public async listCfEvidences(developmentId: string): Promise<DevelopmentDetailDocumentResponse[]> {
        const response = await this.httpClient.request<DevelopmentDetailDocumentResponse[]>({
            url: `/api/developments/${encodeURIComponent(developmentId)}/validation/cf-evidence`,
            method: 'GET'
        });
        return response.body;
    }

    public async listUcEvidences(developmentId: string): Promise<DevelopmentDetailDocumentResponse[]> {
        const response = await this.httpClient.request<DevelopmentDetailDocumentResponse[]>({
            url: `/api/developments/${encodeURIComponent(developmentId)}/validation/uc-evidence`,
            method: 'GET'
        });
        return response.body;
    }

    public async downloadCfEvidence(developmentId: string, docId: string): Promise<{ url: string }> {
        const response = await this.httpClient.request<{ url: string }>({
            url: `/api/developments/${encodeURIComponent(developmentId)}/validation/cf-evidence/${encodeURIComponent(docId)}/download-url`,
            method: 'GET'
        });
        return response.body;
    }

    public async downloadUcEvidence(developmentId: string, docId: string): Promise<{ url: string }> {
        const response = await this.httpClient.request<{ url: string }>({
            url: `/api/developments/${encodeURIComponent(developmentId)}/validation/uc-evidence/${encodeURIComponent(docId)}/download-url`,
            method: 'GET'
        });
        return response.body;
    }

    public async releaseToUc(developmentId: string): Promise<void> {
        await this.httpClient.request({ url: `/api/developments/${encodeURIComponent(developmentId)}/validation/release-to-uc`, method: 'POST' });
    }

    public async approveUcValidation(developmentId: string): Promise<void> {
        await this.httpClient.request({ url: `/api/developments/${encodeURIComponent(developmentId)}/validation/uc-approve`, method: 'POST' });
    }

    public async approveWithoutUc(developmentId: string): Promise<void> {
        await this.httpClient.request({ url: `/api/developments/${encodeURIComponent(developmentId)}/validation/approve-without-uc`, method: 'POST' });
    }

    public async createSolicitation(developmentId: string, params: CreateSolicitationParams): Promise<{ id: string }> {
        const response = await this.httpClient.request<{ id: string }>({
            url: `/api/developments/${encodeURIComponent(developmentId)}/solicitations`,
            method: 'POST',
            body: { type: params.type, title: params.title, description: params.description, steps: params.stepsToReproduce }
        });
        return response.body;
    }

    public async approveSolicitation(developmentId: string, solicitationId: string): Promise<{ jiraWarning: string | null }> {
        const response = await this.httpClient.request<{ jiraWarning?: string | null }>({
            url: `/api/developments/${encodeURIComponent(developmentId)}/solicitations/${encodeURIComponent(solicitationId)}/approve`,
            method: 'POST',
            body: {}
        });
        const warning = response.body?.jiraWarning;
        return { jiraWarning: typeof warning === 'string' && warning.length > 0 ? warning : null };
    }

    public async rejectSolicitation(developmentId: string, solicitationId: string, reasonId: string, notes?: string): Promise<void> {
        await this.httpClient.request({
            url: `/api/developments/${encodeURIComponent(developmentId)}/solicitations/${encodeURIComponent(solicitationId)}/reject`,
            method: 'POST',
            body: { reasonId, notes }
        });
    }

    public async resolveSolicitation(developmentId: string, solicitationId: string, notes: string): Promise<void> {
        await this.httpClient.request({
            url: `/api/developments/${encodeURIComponent(developmentId)}/solicitations/${encodeURIComponent(solicitationId)}/resolve`,
            method: 'POST',
            body: { resolutionNotes: notes }
        });
    }

    public async reclassifySolicitation(developmentId: string, solicitationId: string, newType: string, notes: string): Promise<void> {
        await this.httpClient.request({
            url: `/api/developments/${encodeURIComponent(developmentId)}/solicitations/${encodeURIComponent(solicitationId)}/reclassify`,
            method: 'POST',
            body: { reclassifiedType: newType, ltResponse: notes }
        });
    }

    public async listSolicitationEvidences(developmentId: string, solicitationId: string): Promise<SolicitationEvidenceModel[]> {
        const response = await this.httpClient.request<SolicitationEvidenceResponse[]>({
            url: `/api/developments/${encodeURIComponent(developmentId)}/solicitations/${encodeURIComponent(solicitationId)}/evidence`,
            method: 'GET'
        });
        return SolicitationEvidenceModelClass.withFromResponseList(response.body);
    }

    public async uploadSolicitationEvidence(developmentId: string, solicitationId: string, file: File): Promise<SolicitationEvidenceModel> {
        const formData = new FormData();
        formData.append('file', file);
        const res = await fetch(`/api/developments/${encodeURIComponent(developmentId)}/solicitations/${encodeURIComponent(solicitationId)}/evidence`, {
            method: 'POST',
            body: formData,
            headers: this.authHeaders()
        });
        const data = await res.json().catch(() => null);
        if (!res.ok || !data) {
            throw new Error(data?.message ?? 'Upload failed');
        }
        return SolicitationEvidenceModelClass.withFromResponse(data as SolicitationEvidenceResponse);
    }

    public async removeSolicitationEvidence(developmentId: string, solicitationId: string, docId: string): Promise<void> {
        await this.httpClient.request({
            url: `/api/developments/${encodeURIComponent(developmentId)}/solicitations/${encodeURIComponent(solicitationId)}/evidence/${encodeURIComponent(docId)}`,
            method: 'DELETE'
        });
    }

    public async getSolicitationEvidenceDownloadUrl(developmentId: string, solicitationId: string, docId: string): Promise<{ url: string }> {
        const response = await this.httpClient.request<{ url: string }>({
            url: `/api/developments/${encodeURIComponent(developmentId)}/solicitations/${encodeURIComponent(solicitationId)}/evidence/${encodeURIComponent(docId)}/download-url`,
            method: 'GET'
        });
        return response.body;
    }

    public async loadAudit(developmentId: string): Promise<AuditDataModel> {
        const response = await this.httpClient.request<AuditDataResponse>({ url: `/api/developments/${encodeURIComponent(developmentId)}/audit`, method: 'GET' });
        return AuditDataModelClass.withFromResponse(response.body);
    }

    public async getPbiDecomposition(developmentId: string): Promise<PbiDecompositionModel | null> {
        const response = await this.httpClient.request<PbiDecompositionResponse | null>({
            url: `/api/developments/${encodeURIComponent(developmentId)}/pbi-decomposition`,
            method: 'GET'
        });
        return response.body ? PbiDecompositionModelClass.withFromResponse(response.body) : null;
    }

    public async getAiAnalysisProgress(developmentId: string): Promise<AiAnalysisProgressModel | null> {
        const response = await this.httpClient.request<AiAnalysisProgressResponse | null>({
            url: `/api/developments/${encodeURIComponent(developmentId)}/ai-analysis/progress`,
            method: 'GET'
        });
        return response.body ? AiAnalysisProgressModelClass.withFromResponse(response.body) : null;
    }

    public async retryPbiDecomposition(developmentId: string): Promise<void> {
        await this.httpClient.request({ url: `/api/developments/${encodeURIComponent(developmentId)}/pbi-decomposition/retry`, method: 'POST' });
    }

    public async listJiraEpics(developmentId: string): Promise<JiraEpicSuggestion> {
        const response = await this.httpClient.request<JiraEpicSuggestion>({
            url: `/api/developments/${encodeURIComponent(developmentId)}/jira/epics`,
            method: 'GET'
        });
        return response.body;
    }

    public async linkJiraEpic(developmentId: string, epicKey: string): Promise<JiraEpicLink> {
        const response = await this.httpClient.request<JiraEpicLink>({
            url: `/api/developments/${encodeURIComponent(developmentId)}/jira/link`,
            method: 'POST',
            body: { epicKey }
        });
        return response.body;
    }

    public async retryJira(developmentId: string): Promise<void> {
        await this.httpClient.request({ url: `/api/developments/${encodeURIComponent(developmentId)}/jira/retry`, method: 'POST' });
    }

    public async confirmNoJiraPair(developmentId: string): Promise<JiraNoPairResult> {
        const response = await this.httpClient.request<JiraNoPairResult>({
            url: `/api/developments/${encodeURIComponent(developmentId)}/jira/link/none`,
            method: 'POST'
        });
        return response.body;
    }

    private buildQs(params?: Record<string, string | number | boolean | string[] | undefined | null>): URLSearchParams {
        const qs = new URLSearchParams();
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value === undefined || value === null) {
                    return;
                }
                if (Array.isArray(value)) {
                    if (value.length > 0) {
                        value.forEach((v) => qs.append(key, v));
                    }
                } else {
                    qs.set(key, String(value));
                }
            });
        }
        return qs;
    }

    private authHeaders(): Record<string, string> {
        const token = localStorage.getItem('accessToken');
        return token ? { Authorization: `Bearer ${token}` } : {};
    }
}
